import React, { useMemo, useEffect, useRef } from 'react';
import { Trophy } from 'lucide-react';
import confetti from 'canvas-confetti';
import { useTheme } from '../../contexts/ThemeContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { formatCurrency } from '../../utils/formatters';

const MILESTONES = [10000, 25000, 50000, 100000, 250000, 500000, 1000000, 2500000, 5000000, 10000000];
const MILESTONE_STORAGE_KEY = 'tallyup_last_milestone';

function formatMilestone(val: number): string {
  if (val >= 1000000) return `$${val / 1000000}M`;
  if (val >= 1000) return `$${val / 1000}k`;
  return `$${val}`;
}

export const MilestonesWidget: React.FC = () => {
  const { darkMode, privacyMode } = useTheme();
  const { netWorth } = useFinancialData();
  const hasFiredRef = useRef(false);

  const { reached, next, progress } = useMemo(() => {
    let reached = 0;
    let next = MILESTONES[0];
    for (const m of MILESTONES) {
      if (netWorth >= m) {
        reached = m;
        const idx = MILESTONES.indexOf(m);
        next = idx < MILESTONES.length - 1 ? MILESTONES[idx + 1] : m;
      }
    }
    const progress = next > reached ? ((netWorth - reached) / (next - reached)) * 100 : 100;
    return { reached, next, progress: Math.min(Math.max(progress, 0), 100) };
  }, [netWorth]);

  useEffect(() => {
    if (reached <= 0 || hasFiredRef.current) return;
    const lastSeen = Number(localStorage.getItem(MILESTONE_STORAGE_KEY) || '0');
    if (reached > lastSeen) {
      hasFiredRef.current = true;
      localStorage.setItem(MILESTONE_STORAGE_KEY, String(reached));
      // Fire confetti burst
      const end = Date.now() + 1500;
      const fire = () => {
        confetti({
          particleCount: 80,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#f59e0b', '#fbbf24', '#d97706', '#fcd34d', '#92400e'],
        });
        if (Date.now() < end) requestAnimationFrame(fire);
      };
      fire();
    }
  }, [reached]);

  return (
    <div className={`rounded-xl p-4 ${
      darkMode
        ? 'bg-amber-500/[0.06] border border-amber-500/10'
        : 'bg-amber-500/10 border border-amber-400/25'
    }`}>
      <div className="flex items-center gap-2 mb-2">
        <Trophy size={14} className="text-amber-400" />
        <p className={`text-xs font-medium ${darkMode ? 'text-amber-300' : 'text-amber-600'}`}>Milestone</p>
      </div>
      {reached > 0 ? (
        <>
          <p className={`text-sm md:text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            {privacyMode ? 'Milestone Reached' : formatMilestone(reached)}
          </p>
          {!privacyMode && reached !== next && (
            <div className="mt-2">
              <div className="flex justify-between mb-0.5">
                <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  Next: {formatMilestone(next)}
                </span>
                <span className={`text-[10px] font-semibold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                  {progress.toFixed(0)}%
                </span>
              </div>
              <div className={`h-1.5 rounded-full overflow-hidden ${darkMode ? 'bg-white/10' : 'bg-amber-200/50'}`}>
                <div className="h-full rounded-full bg-amber-400 transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
        </>
      ) : (
        <p className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
          {privacyMode ? '••••••' : `${formatCurrency(netWorth)} / ${formatMilestone(next)}`}
        </p>
      )}
    </div>
  );
};
